import { Link, Outlet, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Utensils, ClipboardList, CreditCard, AlertTriangle, ChevronRight, ChevronDown, Settings, Shield, Calculator, Layout, Tag, Coins, Mail, MapPin, ShoppingBag, Palette, Share2, BarChart3, PenSquare, CalendarDays, MessageSquare, Megaphone } from 'lucide-react';
import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';

export default function AdminLayout() {
    const location = useLocation();
    const { tenant } = useTenant();
    const { user } = useAuth();
    const [isPaymentConfigured, setIsPaymentConfigured] = useState(true);
    const [isStoreConfigured, setIsStoreConfigured] = useState(true);
    const [isTestMode, setIsTestMode] = useState(false);
    const [marketingOpen, setMarketingOpen] = useState(() => location.pathname.startsWith('/admin/social-media') || location.pathname.startsWith('/admin/campaigns'));

    const checkConfigs = async () => {
        if (!user || user.role === 'franchise_admin') return;
        try {
            const [paymentRes, operationalRes] = await Promise.all([
                api.get('/api/restaurants/settings/payment'),
                api.get('/api/restaurants/settings/operational')
            ]);
            
            // Payment Check
            const paymentData = paymentRes.data;
            const configs = paymentData.configs || [];
            const testMode = paymentData.useTestPayment || false;
            
            const hasPayment = configs.some(c => c.is_active && c.config);
            setIsPaymentConfigured(hasPayment || testMode);
            setIsTestMode(testMode);

            // Location/Store Settings Check
            const opData = operationalRes.data;
            const hasLocation = opData && opData.latitude && opData.longitude;
            setIsStoreConfigured(!!hasLocation);
        } catch (err) {
            console.error('Config checks failed:', err);
        }
    };

    useEffect(() => {
        checkConfigs();
        
        // Listen for immediate updates from children
        window.addEventListener('paymentConfigUpdated', checkConfigs);
        window.addEventListener('configUpdated', checkConfigs);
        return () => {
            window.removeEventListener('paymentConfigUpdated', checkConfigs);
            window.removeEventListener('configUpdated', checkConfigs);
        };
    }, [location.pathname, user]);

    const isActive = (path) => location.pathname === path || (path !== '/admin' && location.pathname.startsWith(path));

    // Auto-open Marketing group when on a marketing page
    useEffect(() => {
        if (location.pathname.startsWith('/admin/social-media') || location.pathname.startsWith('/admin/campaigns')) {
            setMarketingOpen(true);
        }
    }, [location.pathname]);

    const allNavItems = user?.role === 'franchise_admin' ? [
        { name: 'Overview', path: '/admin', icon: LayoutDashboard },
        { name: 'Branch Directory', path: '/admin/branches', icon: MapPin },
        { name: 'Transactions', path: '/admin/transactions', icon: ShoppingBag },
        { name: 'Branding Settings', path: '/admin/branding', icon: Palette }
    ] : [
        { name: 'Overview', path: '/admin', icon: LayoutDashboard },
        { name: 'POS Billing', path: '/admin/pos', icon: Calculator },
        { name: 'Menu Items', path: '/admin/menu', icon: Utensils },
        { name: 'Orders', path: '/admin/orders', icon: ClipboardList },
        { name: 'Table Bookings', path: '/admin/reservations', icon: CalendarDays },
        { name: 'Coupons & Offers', path: '/admin/coupons', icon: Tag },
        { name: 'Homepage Editor', path: '/admin/homepage', icon: Layout, hideForCustom: true },
        { name: 'Users & Staff', path: '/admin/users', icon: Users },
        { name: 'Payroll & Salary', path: '/admin/payroll', icon: Coins },
        { name: 'Payments', path: '/admin/payment-settings', icon: CreditCard },
        { name: 'Store Settings', path: '/admin/settings', icon: Settings },
        { name: 'Subscription', path: '/admin/subscription', icon: Shield },
    ];

    const marketingItems = [
        { name: 'Dashboard', path: '/admin/social-media', icon: Share2 },
        { name: 'Create Post', path: '/admin/social-media/compose', icon: PenSquare },
        { name: 'Social Inbox', path: '/admin/social-media/comments', icon: MessageSquare },
        { name: 'Analytics', path: '/admin/social-media/analytics', icon: BarChart3 },
        { name: 'Calendar', path: '/admin/social-media/calendar', icon: CalendarDays },
        { name: 'Email Campaigns', path: '/admin/campaigns', icon: Mail },
    ];

    const isMarketingActive = marketingItems.some(i => isActive(i.path));

    const navItems = allNavItems.filter(item => !(item.hideForCustom && tenant?.website_type === 'custom'));

    return (
        <div className="flex flex-col min-h-[calc(100vh-4rem)]">
            {!isStoreConfigured && user?.role !== 'franchise_admin' && (
                <div className="bg-red-600 text-white px-4 py-2 flex items-center justify-between text-sm shadow-md">
                    <div className="flex items-center space-x-3">
                        <div className="bg-white/20 p-1 rounded-md">
                            <Settings className="w-4 h-4" />
                        </div>
                        <div>
                            <span className="font-bold">Action Required:</span>
                            <span className="ml-2 opacity-90 hidden sm:inline">Store location is not set. Online orders and delivery calculations are disabled.</span>
                            <span className="ml-2 opacity-90 sm:hidden">Store location not set.</span>
                        </div>
                    </div>
                    <Link 
                        to="/admin/settings" 
                        className="flex items-center space-x-1 bg-white text-red-700 px-3 py-1 rounded-lg font-bold hover:bg-opacity-90 transition-all text-xs"
                    >
                        <span>Set Location</span>
                        <ChevronRight className="w-3 h-3" />
                    </Link>
                </div>
            )}

            {!isPaymentConfigured && user?.role !== 'franchise_admin' && (
                <div className="bg-amber-600 text-white px-4 py-2 flex items-center justify-between text-sm shadow-md animate-pulse-subtle">
                    <div className="flex items-center space-x-3">
                        <div className="bg-white/20 p-1 rounded-md">
                            <AlertTriangle className="w-4 h-4" />
                        </div>
                        <div>
                            <span className="font-bold">Action Required:</span>
                            <span className="ml-2 opacity-90 hidden sm:inline">Your payment gateway is not configured. You cannot accept online payments.</span>
                            <span className="ml-2 opacity-90 sm:hidden">Payment gateway not configured.</span>
                        </div>
                    </div>
                    <Link 
                        to="/admin/payment-settings" 
                        className="flex items-center space-x-1 bg-white text-amber-700 px-3 py-1 rounded-lg font-bold hover:bg-opacity-90 transition-all text-xs"
                    >
                        <span>Configure Now</span>
                        <ChevronRight className="w-3 h-3" />
                    </Link>
                </div>
            )}

            {isTestMode && user?.role !== 'franchise_admin' && (
                <div className="bg-primary-600 text-white px-4 py-2 flex items-center justify-between text-sm shadow-md border-b border-primary-700">
                    <div className="flex items-center space-x-3 text-white">
                        <div className="bg-white/20 p-1 rounded-md text-white">
                            <CreditCard className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <span className="font-bold text-white">Test Mode Active:</span>
                            <span className="ml-2 opacity-90 hidden sm:inline text-white">Using Aaharam Cloud sandbox keys. Payments will not be credited to your account.</span>
                            <span className="ml-2 opacity-90 sm:hidden text-white">Using Aaharam Sandbox.</span>
                        </div>
                    </div>
                </div>
            )}
            
            <div className="flex flex-col md:flex-row flex-1">
                <aside className="w-64 bg-white border-r hidden md:block overflow-y-auto">
                    <nav className="p-4 space-y-1">
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            return (
                                 <Link
                                     key={item.path}
                                     to={item.path}
                                     className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                                         isActive(item.path) ? 'font-medium' : 'text-gray-600 hover:bg-gray-50'
                                     }`}
                                     style={isActive(item.path) ? {
                                         backgroundColor: 'var(--sidebar-active-bg, rgba(91, 67, 16, 0.1))',
                                         color: 'var(--sidebar-active-text, var(--primary-600))'
                                     } : {}}
                                 >
                                     <Icon className="w-5 h-5" />
                                     <span>{item.name}</span>
                                 </Link>
                            );
                        })}

                        {/* Marketing Collapsible Group */}
                        {user?.role !== 'franchise_admin' && (
                            <div>
                                <button
                                    onClick={() => setMarketingOpen(o => !o)}
                                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                                        isMarketingActive
                                            ? 'font-semibold'
                                            : 'text-gray-600 hover:bg-gray-50'
                                    }`}
                                    style={isMarketingActive ? {
                                        backgroundColor: 'var(--sidebar-active-bg, rgba(91, 67, 16, 0.08))',
                                        color: 'var(--sidebar-active-text, var(--primary-600))'
                                    } : {}}
                                >
                                    <div className="flex items-center space-x-3">
                                        <Megaphone className="w-5 h-5" />
                                        <span>Marketing</span>
                                    </div>
                                    <ChevronDown
                                        className={`w-4 h-4 transition-transform duration-200 ${
                                            marketingOpen ? 'rotate-180' : ''
                                        }`}
                                    />
                                </button>

                                {marketingOpen && (
                                    <div className="mt-1 ml-3 pl-3 border-l-2 border-gray-100 space-y-0.5">
                                        {marketingItems.map(item => {
                                            const Icon = item.icon;
                                            const active = isActive(item.path);
                                            return (
                                                <Link
                                                    key={item.path}
                                                    to={item.path}
                                                    className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                                                        active ? 'font-medium' : 'text-gray-600 hover:bg-gray-50'
                                                    }`}
                                                    style={active ? {
                                                        backgroundColor: 'var(--sidebar-active-bg, rgba(91, 67, 16, 0.1))',
                                                        color: 'var(--sidebar-active-text, var(--primary-600))'
                                                    } : {}}
                                                >
                                                    <Icon className="w-4 h-4" />
                                                    <span>{item.name}</span>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </nav>
                </aside>
                <main className="flex-1 p-6 bg-gray-50 overflow-auto pb-20 md:pb-6">
                    <Outlet />
                </main>
            </div>

            {/* Mobile Bottom Navigation */}
            <nav className="md:hidden fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 z-50 overflow-x-auto safe-area-bottom">
                <div className="flex justify-around items-center h-16 min-w-max px-2">
                    {navItems.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.path);
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex flex-col items-center justify-center px-3 space-y-1 transition-colors ${
                                    active ? '' : 'text-gray-500 hover:text-gray-900'
                                }`}
                                style={active ? { color: 'var(--sidebar-active-text, var(--primary-600))' } : {}}
                            >
                                <Icon className="w-5 h-5" />
                                <span className="text-[10px] font-medium whitespace-nowrap">{item.name}</span>
                            </Link>
                        );
                    })}
                    {/* Marketing shortcut for mobile */}
                    {user?.role !== 'franchise_admin' && (
                        <Link
                            to="/admin/social-media"
                            className={`flex flex-col items-center justify-center px-3 space-y-1 transition-colors ${
                                isMarketingActive ? '' : 'text-gray-500 hover:text-gray-900'
                            }`}
                            style={isMarketingActive ? { color: 'var(--sidebar-active-text, var(--primary-600))' } : {}}
                        >
                            <Megaphone className="w-5 h-5" />
                            <span className="text-[10px] font-medium whitespace-nowrap">Marketing</span>
                        </Link>
                    )}
                </div>
            </nav>
        </div>
    );
}
